// src/pages/SelectionOrders/components/tables/OrderIncidentsTab.tsx
import React, { useEffect, useMemo, useState } from 'react';
import DefaultCard from '../../../../components/default/DefaultCard';
import LazyLoading from '../../../../components/LazyLoading';
import useSelectionOrderService from '../../../../app/services/hooks/useSelectionOrderService';
import useIncidentReasonService from '../../../../app/services/hooks/useIncidentReasonService';
import { IncidentReasonDataForm } from '../../../../app/models/IncidentReason';
import { ServiceResponse } from '../../../../app/services/shared/interfaces';
import { toast } from 'react-toastify';
import { Modal, Button, Form } from 'react-bootstrap';
import ButtonTableAction from '../../../../components/buttons/ButtonTableAction';
import useReactConfirmAlert from '../../../../hooks/useReactConfirmAlert';

type Props = {
  orderId: number;
  onChanged?: () => void;
  show?: (id: number) => void;
  edit?: (id: number) => void;
  destroy?: (id: number) => void;
};

type IncidentRow = {
  id: number;
  date: string;
  time: string;
  reason?: number; // ID del motivo
  reason_id?: number;
  reason_label?: string;
  description: string;
  solution?: string|null;
  status: 'OPEN'|'CLOSED'|string;
  status_label?: string;
  evidence_url?: string|null;
  evidence_name?: string|null;
  created_at: string;
};

const STATUSES = [
  { id: 'OPEN', name: 'Abierto' },
  { id: 'CLOSED', name: 'Cerrado' },
];

const OrderIncidentsTab: React.FC<Props> = ({ orderId, onChanged, show, edit, destroy }) => {
  const {
    getOrderIncidents,
    storeOrderIncident,
    updateOrderIncident,
    deleteOrderIncident,
    fetchingGetOrderIncidents,
    fetchingStoreOrderIncident,
    fetchingUpdateOrderIncident,
    fetchingDeleteOrderIncident,
  } = useSelectionOrderService();
  
  const { getAllIncidentReasons } = useIncidentReasonService();
  const [reasons, setReasons] = useState<IncidentReasonDataForm[]>([]);

  const [rows, setRows] = useState<IncidentRow[]>([]);
  const [page, setPage] = useState<number>(1);
  const [pageSize] = useState<number>(15);
  const [total, setTotal] = useState<number>(0);

  const totalPages = useMemo(() => Math.max(1, Math.ceil(total / pageSize)), [total, pageSize]);
  const confirm = useReactConfirmAlert();

  // ---- Modal crear/editar ----
  const [showModal, setShowModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);

  const [form, setForm] = useState<{
    date: string;
    time: string;
    reason_id: number | '';
    description: string;
    solution?: string;
    status: string;
    evidence?: File | null;
  }>({
    date: '',
    time: '',
    reason_id: '',
    description: '',
    solution: '',
    status: 'OPEN',
    evidence: null,
  });

  // Cargar motivos cuando se abre el modal
  useEffect(() => {
    if (showModal) {
      getAllIncidentReasons(
        { page: 1, per_page: 100, sort: 'id', order: 'asc', is_active: true },
        {
          onSuccess: (response: ServiceResponse) => {
            setReasons(response.data.incident_reasons || []);
          }
        }
      );
    }
  }, [showModal]);

  const openCreate = () => { resetForm(); setIsEditing(false); setEditingId(null); setShowModal(true); };
  const openEdit = (r: IncidentRow) => {
    setIsEditing(true);
    setEditingId(r.id);
    setForm({
      date: r.date,
      time: (r.time || '').slice(0,5), // HH:MM
      reason_id: r.reason_id || r.reason || '',
      description: r.description ?? '',
      solution: r.solution ?? '',
      status: String(r.status),
      evidence: null,
    });
    setShowModal(true);
  };
  const closeModal = () => setShowModal(false);

  const load = (p = 1) => {
    getOrderIncidents(
      orderId,
      { page: p, page_size: pageSize },
      {
        onSuccess: (resp: ServiceResponse) => {
          const list = resp.data?.results ?? resp.data ?? [];
          setRows(list);
          setTotal(resp.data?.count ?? list.length ?? 0);
        },
        onError: (resp: ServiceResponse) => {
          toast.error(resp.message || 'No se pudo cargar incidentes.');
        },
      }
    );
  };

  useEffect(() => { load(1); setPage(1); }, [orderId]);

  const resetForm = () => {
    setForm({
      date: '',
      time: '',
      reason_id: '',
      description: '',
      solution: '',
      status: 'OPEN',
      evidence: null,
    });
  };

  const validateForm = () => {
    if (!form.date) { toast.error('La Fecha es obligatoria.'); return false; }
    if (!form.time) { toast.error('La Hora es obligatoria.'); return false; }
    if (!form.reason_id) { toast.error('El Motivo es obligatorio.'); return false; }
    if (!form.description.trim()) { toast.error('La Descripción es obligatoria.'); return false; }
    if (form.status === 'CLOSED' && !form.solution?.trim()) {
      toast.error('Para cerrar el incidente debe indicar la Solución.');
      return false;
    }
    return true;
  };

  const buildPayload = () => {
    const hasFile = !!form.evidence;
    if (hasFile) {
      const fd = new FormData();
      fd.append('date', form.date);
      fd.append('time', form.time.length === 5 ? `${form.time}:00` : form.time);
      fd.append('reason_id', String(form.reason_id));
      fd.append('description', form.description);
      fd.append('status', form.status);
      if (form.solution) fd.append('solution', form.solution);
      fd.append('evidence', form.evidence!);
      return fd;
    }
    return {
      date: form.date,
      time: form.time.length === 5 ? `${form.time}:00` : form.time,
      reason_id: form.reason_id,
      description: form.description,
      status: form.status,
      ...(form.solution ? { solution: form.solution } : {}),
    };
  };

  const handleSave = () => {
    if (!validateForm()) return;
    const payload = buildPayload();

    if (isEditing && editingId) {
      updateOrderIncident(editingId, payload as any, {
        onSuccess: (resp: ServiceResponse) => {
          toast.success(resp.message || 'Incidente actualizado con éxito.');
          closeModal();
          resetForm();
          load(page);
          onChanged?.();
        },
        onError: (resp: ServiceResponse) => {
          toast.error(resp.message || 'No se pudo actualizar el incidente.');
        },
      });
    } else {
      storeOrderIncident(orderId, payload as any, {
        onSuccess: (resp: ServiceResponse) => {
          toast.success(resp.message || 'Incidente creado con éxito.');
          closeModal();
          resetForm();
          load(1);
          setPage(1);
          onChanged?.();
        },
        onError: (resp: ServiceResponse) => {
          toast.error(resp.message || 'No se pudo crear el incidente.');
        },
      });
    }
  };

  const destroyIncident = (id: number) => {
    const _text = `Está a punto de eliminar el incidente #${id}`;
    confirm.requestConfirmation({
      title: '¿Estás seguro?',
      message: _text,
      buttons: {
        confirmButton: {
          label: 'Sí, confirmar',
          onClick: () => {
            deleteOrderIncident(id, {
              onSuccess: (response: ServiceResponse) => {
                confirm.successAlert({ title: 'Éxito', message: response.message });
                load(page);
                onChanged?.();
              },
              onError: (response: ServiceResponse) => {
                confirm.errorAlert({ title: 'Error', message: response.message });
              }
            });
          }
        },
        cancelButton: {
          label: 'No, cancelar',
          onClick: () => {
            setTimeout(() => {
              confirm.infoAlert({ title: 'Cancelado', message: 'El incidente no se ha eliminado.' });
            }, 0);
          }
        }
      }
    });
  };

  const renderFile = (r: IncidentRow) => {
    if (!r.evidence_url) return <span className="text-muted">-</span>;
    return (
      <a href={r.evidence_url} target="_blank" rel="noreferrer">
        {r.evidence_name || 'ver archivo'}
      </a>
    );
  };

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-2">
        <h5 className="mb-0">Incidentes</h5>
        <div className="btn-group">
          <button
            className="btn btn-primary btn-sm"
            onClick={openCreate}
            disabled={!!(fetchingGetOrderIncidents || fetchingStoreOrderIncident || fetchingUpdateOrderIncident)}
          >
            + Nueva incidencia
          </button>
        </div>
      </div>

      <DefaultCard>
        {fetchingGetOrderIncidents && rows.length === 0 ? (
          <LazyLoading height={200} />
        ) : (
          <div className="table-responsive">
            <table className="table table-hover align-middle">
              <thead>
                <tr>
                  <th style={{ width: 120 }}>Fecha</th>
                  <th style={{ width: 90 }}>Hora</th>
                  <th style={{ width: 180 }}>Motivo</th>
                  <th>Descripción</th>
                  <th style={{ width: 120 }}>Estado</th>
                  <th style={{ width: 160 }}>Evidencia</th>
                  <th style={{ width: 140 }} className="text-end">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && (
                  <tr><td colSpan={7} className="text-muted">Sin incidencias registradas.</td></tr>
                )}
                {rows.map((r) => (
                  <tr key={r.id}>
                    <td>{new Date(r.date).toLocaleDateString()}</td>
                    <td>{r.time?.slice(0,5)}</td>
                    <td>{r.reason_label ?? r.reason}</td>
                    <td className="text-truncate" style={{ maxWidth: 420 }}>{r.description}</td>
                    <td>{r.status_label ?? r.status}</td>
                    <td>{renderFile(r)}</td>
                    <td className="text-end">
                      {show && (
                        <ButtonTableAction
                          callbackFunction={() => show(r.id ?? -1)}
                          classIcon="mdi mdi-eye"
                          colorIcon="text-info"
                          title="Ver"
                        />
                      )}
                      <ButtonTableAction
                        callbackFunction={() => (edit ? edit(r.id ?? -1) : openEdit(r))}
                        classIcon="mdi mdi-pencil"
                        colorIcon="text-warning"
                        title="Editar"
                      />
                      <ButtonTableAction
                        callbackFunction={() => (destroy ? destroy(r.id ?? -1) : destroyIncident(r.id))}
                        classIcon="mdi mdi-delete"
                        colorIcon="text-danger"
                        title="Eliminar"
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {totalPages > 1 && (
              <div className="d-flex justify-content-between align-items-center mt-2">
                <div className="text-muted">Página {page} de {totalPages}</div>
                <div className="btn-group">
                  <button
                    className="btn btn-outline-secondary btn-sm"
                    disabled={page <= 1}
                    onClick={() => { const p = page - 1; setPage(p); load(p); }}
                  >
                    « Anterior
                  </button>
                  <button
                    className="btn btn-outline-secondary btn-sm"
                    disabled={page >= totalPages}
                    onClick={() => { const p = page + 1; setPage(p); load(p); }}
                  >
                    Siguiente »
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </DefaultCard>

      {/* Modal crear/editar */}
      <Modal show={showModal} onHide={closeModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>{isEditing ? 'Editar incidencia' : 'Nueva incidencia'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <div className="row">
              <div className="col-md-6 mb-3">
                <Form.Label>Fecha</Form.Label>
                <Form.Control
                  type="date"
                  value={form.date}
                  onChange={(e) => setForm({ ...form, date: e.target.value })}
                />
              </div>
              <div className="col-md-6 mb-3">
                <Form.Label>Hora</Form.Label>
                <Form.Control
                  type="time"
                  value={form.time}
                  onChange={(e) => setForm({ ...form, time: e.target.value })}
                />
              </div>
              <div className="col-md-6 mb-3">
                <Form.Label>Motivo</Form.Label>
                <Form.Select
                  value={form.reason_id}
                  onChange={(e) => setForm({ ...form, reason_id: e.target.value ? Number(e.target.value) : '' })}
                >
                  <option value="">Seleccione un motivo</option>
                  {reasons.map(r => (
                    <option key={r.id} value={r.id}>{r.name}</option>
                  ))}
                </Form.Select>
              </div>
              <div className="col-md-6 mb-3">
                <Form.Label>Estado</Form.Label>
                <Form.Select
                  value={form.status}
                  onChange={(e) => setForm({ ...form, status: e.target.value })}
                >
                  {STATUSES.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </Form.Select>
              </div>
              <div className="col-12 mb-3">
                <Form.Label>Descripción</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                />
              </div>
              <div className="col-12 mb-3">
                <Form.Label>Solución (requerida si cierras)</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  value={form.solution}
                  onChange={(e) => setForm({ ...form, solution: e.target.value })}
                />
              </div>
              <div className="col-12 mb-2">
                <Form.Label>Evidencia (archivo)</Form.Label>
                <Form.Control
                  type="file"
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setForm({ ...form, evidence: (e.target.files && e.target.files[0]) || null })
                  }
                />
              </div>
            </div>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeModal}>Cancelar</Button>
          <Button
            variant="primary"
            onClick={handleSave}
            disabled={!!(fetchingStoreOrderIncident || fetchingUpdateOrderIncident)}
          >
            {isEditing ? 'Guardar cambios' : 'Guardar'}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default OrderIncidentsTab;
